import React, {useCallback, useEffect, useRef, useState} from "react";
import {approveBroker, listBrokers, rejectBroker, suspendBroker} from "../api/admin";

const STATUS_TABS = ["PENDING", "APPROVED", "REJECTED", "SUSPENDED", "ALL"];
const TONE = {APPROVED: "green", PENDING: "amber", REJECTED: "red", SUSPENDED: "grey"};

/** Broker accounts: approve or reject new registrations, suspend an approved one (hides all their listings). */
const BrokersTable = ({onAuthError}) => {
    const [status, setStatus] = useState("PENDING");
    const [brokers, setBrokers] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null);
    const [confirming, setConfirming] = useState(null);

    const shownStatus = useRef(status);
    shownStatus.current = status;

    // Always loads the filter on screen now: an action finishing after the filter changed mustn't show the old one.
    const load = useCallback(async () => {
        const wanted = shownStatus.current;
        setError("");
        try {
            const rows = await listBrokers(wanted);
            if (shownStatus.current === wanted) setBrokers(rows);
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        }
    }, [onAuthError]);

    useEffect(() => { setBrokers(null); load(); }, [status, load]);

    const act = async (broker, fn) => {
        setBusy(broker.id);
        setError("");
        try {
            await fn(broker.id);
            await load();
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        } finally {
            setBusy(null);
            setConfirming(null);
        }
    };

    return (
        <div>
            <div className="brk-card__head">
                <div className="brk-filters" role="group" aria-label="Filter brokers by status">
                    {STATUS_TABS.map((s) => (
                        <button key={s} type="button" aria-pressed={status === s}
                                className={`brk-filter${status === s ? " brk-filter--on" : ""}`} onClick={() => setStatus(s)}>
                            {s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
                        </button>
                    ))}
                </div>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={load}>Refresh</button>
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            {!brokers ? <p className="brk-empty">Loading…</p> : brokers.length === 0 ? (
                <p className="brk-empty">No brokers with this status.</p>
            ) : (
                <div className="brk-table-wrap">
                    <table className="brk-table">
                        <thead>
                        <tr><th>Name</th><th>Type</th><th>Company</th><th>Contact</th><th>RERA</th><th>Areas</th><th>Status</th><th>Registered</th><th/></tr>
                        </thead>
                        <tbody>
                        {brokers.map((b) => (
                            <tr key={b.id}>
                                <td className="brk-strong">{b.fullName}</td>
                                <td>{b.accountType === "BUILDER" ? "Builder" : "Agent"}</td>
                                <td>{b.companyName}</td>
                                <td><div>{b.mobile}</div><div className="brk-hint">{b.email}</div></td>
                                <td>{b.reraNumber || "—"}</td>
                                <td className="brk-hint">{(b.operatingAreas || []).join(", ") || "—"}</td>
                                <td><span className={`brk-badge brk-badge--${TONE[b.status] || "grey"}`}>{b.status}</span>
                                    {b.authProvider === "WHATSAPP" && <div className="brk-hint">via WhatsApp, unverified</div>}</td>
                                <td>{b.createdAt ? new Date(b.createdAt).toLocaleDateString() : "—"}</td>
                                <td>
                                    {confirming?.id === b.id ? (
                                        <span className="brk-row-actions">
                                            <span className="brk-hint">{confirming.label}?</span>
                                            <button type="button" className="brk-link brk-link--danger" disabled={busy === b.id}
                                                    onClick={() => act(b, confirming.fn)}>Yes</button>
                                            <button type="button" className="brk-link" onClick={() => setConfirming(null)}>No</button>
                                        </span>
                                    ) : (
                                        <span className="brk-row-actions">
                                            {b.status !== "APPROVED" && (
                                                <button type="button" className="brk-link" disabled={busy === b.id} onClick={() => act(b, approveBroker)}>
                                                    {b.status === "PENDING" ? "Approve" : "Re-approve"}
                                                </button>
                                            )}
                                            {b.status === "PENDING" && (
                                                <button type="button" className="brk-link brk-link--danger"
                                                        onClick={() => setConfirming({id: b.id, label: "Reject", fn: rejectBroker})}>Reject</button>
                                            )}
                                            {b.status === "APPROVED" && (
                                                <button type="button" className="brk-link brk-link--danger"
                                                        onClick={() => setConfirming({id: b.id, label: "Suspend and hide their listings", fn: suspendBroker})}>
                                                    Suspend
                                                </button>
                                            )}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BrokersTable;
